// External libraries
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

// FontAwesome icons
import { faHome } from "@fortawesome/pro-duotone-svg-icons";

// Kendo React components
import { Breadcrumb, Splitter } from "@progress/kendo-react-layout";

// Internal components
import { ConditionBuilderWindow } from "../Common/ConditionBuilder/ConditionBuilderWindow";
import { ConditionRowBuilderWindow } from "../Common/ConditionBuilder/ConditionRowBuilderWindow";
import { ExpressionBuilderWindow } from "../Common/ExpressionBuilder/ExpressionBuilderWindow";
import { GalleryConstructorWindow } from "../Common/GalleryConstructor/GalleryConstructorWindow";
import { ColorChangingIcon } from "../Common/UI/ColorChangingIcon";
import { Else, If } from "../Common/UI/IfComponent";
import { CodeEditor } from "../Common/UI/JsonCodeEditor";
import { VisbilityBuilderWindow } from "../Common/VisibilityRules/VisibilityBuilderWindow";
import { ColumnsConstructorWindow } from "../Fields/ColumnsConstructor/ColumnsConstructorWindow";
import { AutomationsWindow } from "./TemplateAutomations/AutomationWindow";
import { TemplateForm } from "./TemplateForm";
import { TemplateInformationPane } from "./TemplateInformationPane";
import { TemplatePropertyPane } from "./TemplatePropertyPane";
import { TemplateRibbonBar } from "./TemplateRibbonBar";

// Signals
import { allTemplates } from "../Common/Store/CommonSignals";
import { selectedHamburgerIndex } from "../Navigation/HamburgerMenu/HamburgerMenuSignals";
import {
  isTemplatePersisted,
  nestedPanes,
  templateData,
  templateEditMode,
  templatePaneSize,
} from "./NewTemplateSignals";
import { TemplateAuditHistory } from "./TemplateHistory/TemplateAuditHistory";

function NewTemplate() {
  const navigate = useNavigate();
  selectedHamburgerIndex.value = 3;
  const urlParams = new URLSearchParams(window.location.search);
  const templateId = urlParams.get("templateId");

  useEffect(() => {
    if (!allTemplates.value || allTemplates.value.length === 0) return;
    if (templateId) {
      const template = allTemplates.value.find((t) => t.id === templateId) ?? null;
      if (template) {
        templateData.value = template;
      } else {
        navigate("/templates");
      }
    } else if (window.location.pathname.includes("/templates/edit")) {
      navigate("/templates");
    }
  }, [templateId, allTemplates.value]);

  if (window.location.pathname.includes("/templates/edit")) {
    if (!templateData.value) return null;
    if (!isTemplatePersisted.value) return null;
  }

  return (
    <div className="!k-d-flex !k-flex-col !k-justify-content-start !k-rounded-lg !k-w-full !k-h-full">
      <Breadcrumb
        className="!k-mb-2"
        style={{ backgroundColor: "transparent" }}
        data={[
          {
            id: "home",
            icon: (
              <ColorChangingIcon
                icon={faHome}
                initialPrimaryColor="#0078D4"
                initialSecondaryColor="#00aeef"
                initialSize={16}
                hoverSize={16}
              />
            ),
          },
          { id: "viewTemplates_Highlight", text: "Templates" },
          {
            id: "newTemplates_Edge",
            text: isTemplatePersisted.value ? "Edit Template" : "Create New Template",
          },
        ]}
        onItemSelect={(e) => {
          if (e.id === "home") {
            navigate("/");
          } else if (e.id.includes("viewTemplates")) {
            navigate("/templates");
          }
        }}
      />
      <TemplateRibbonBar />
      <If condition={templateEditMode.value === "Visual"}>
        <Splitter
          className="!k-h-full !k-w-full !k-overflow-y-auto"
          orientation="horizontal"
          panes={nestedPanes.value}
          onChange={(e) => {
            templatePaneSize.value = e.newState;
          }}
        >
          <TemplateInformationPane />
          <TemplateForm />
          <TemplatePropertyPane />
        </Splitter>
        <Else>
          <CodeEditor
            initialCode={templateData.value}
            language={"json"}
            type={"Template"}
          />
        </Else>
      </If>
      <AutomationsWindow />
      <GalleryConstructorWindow />
      <ExpressionBuilderWindow />
      <ConditionBuilderWindow />
      <ConditionRowBuilderWindow />
      <VisbilityBuilderWindow />
      <ColumnsConstructorWindow />
      <TemplateAuditHistory />
    </div>
  );
}

export default NewTemplate;
